'use client';

import type { FrecencyEntry, FrecencyEntryType } from '@/hooks/use-frecency';
import {
  ArrowRight,
  Building2,
  CalendarClock,
  CircleUserRound,
  FolderKanban,
  Layers,
  MessageSquare,
  MessagesSquare,
  Package,
  Users,
  Zap,
  SwatchBook,
} from 'lucide-react';
import type { ComponentType } from 'react';
import { useLocale } from 'next-intl';
import { getConnectSearchCopy } from '@/i18n/connect-search-copy';
import {
  ItemIcon,
  MenuGroup,
  MenuItem,
  MenuKey,
  ShortcutHint,
} from '../primitives';
import {
  AestheticRows,
  AvatarRows,
  CommandRows,
  LinkRows,
  MemberRows,
  ProductRows,
  ProjectRows,
  ThreadRows,
} from '../sections';
import type {
  AestheticResult,
  AvatarResult,
  CommandDef,
  GoToItem,
  LinkResult,
  MemberResult,
  OrgResult,
  OrgThreadResult,
  ProductResult,
  ProjectResult,
  SearchScope,
} from '../types';
import { LINK_SCOPES } from '../types';
import { useLinkScopeConfigs } from '../link-scopes';

export type RootPageText = {
  recent: string;
  browse: string;
  recentProducts: string;
  recentThreads: string;
  goTo: string;
  products: string;
  threads: string;
  projects: string;
  avatars: string;
  aesthetics: string;
  members: string;
  scheduled: string;
  quickActions: string;
  organizations: string;
};

const RECENT_ICONS: Record<
  FrecencyEntryType,
  ComponentType<{ className?: string }>
> = {
  product: Package,
  thread: MessageSquare,
  project: FolderKanban,
  collection: Layers,
  avatar: CircleUserRound,
  aesthetic: SwatchBook,
  member: Users,
  workspace: Building2,
  navigation: ArrowRight,
  action: Zap,
};

export function RootPage({
  text,
  shouldSearch,
  recentEntries,
  products,
  threads,
  messages,
  orgs,
  projects,
  avatars,
  aesthetics,
  members,
  gotoItems,
  canSearchWorkspaceScopes,
  roleLabels,
  quickActions,
  formatTime,
  onNavigate,
  onSelectRecent,
  onSelectProduct,
  onSelectThread,
  onSelectLink,
  onSelectOrg,
  onSelectProject,
  onSelectAvatar,
  onSelectAesthetic,
  onSelectMember,
  onPushScope,
  onRunCommand,
}: {
  text: RootPageText;
  shouldSearch: boolean;
  recentEntries: FrecencyEntry[];
  products: ProductResult[];
  threads: OrgThreadResult[];
  /** Connect message hits for the typed term (opens the message). */
  messages: LinkResult[];
  orgs: OrgResult[];
  projects: ProjectResult[];
  avatars: AvatarResult[];
  aesthetics: AestheticResult[];
  members: MemberResult[];
  gotoItems: GoToItem[];
  canSearchWorkspaceScopes: boolean;
  roleLabels: Record<'owner' | 'admin' | 'member', string>;
  quickActions: CommandDef[];
  formatTime: (timestamp: number) => string;
  onNavigate: (href: string, label?: string) => void;
  onSelectRecent: (entry: FrecencyEntry) => void;
  onSelectProduct: (product: ProductResult) => void;
  onSelectThread: (thread: OrgThreadResult) => void;
  onSelectLink: (link: LinkResult) => void;
  onSelectOrg: (org: OrgResult) => void;
  onSelectProject: (project: ProjectResult) => void;
  onSelectAvatar: (avatar: AvatarResult) => void;
  onSelectAesthetic: (aesthetic: AestheticResult) => void;
  onSelectMember: (member: MemberResult) => void;
  onPushScope: (scope: SearchScope) => void;
  onRunCommand: (command: CommandDef) => void;
}) {
  const linkConfigs = useLinkScopeConfigs();
  const messagesHeading = getConnectSearchCopy(useLocale()).messagesHeading;
  const showBrowse =
    !shouldSearch &&
    (products.length > 0 || threads.length > 0 || canSearchWorkspaceScopes);

  const workspaceScopes: Array<{
    scope: SearchScope;
    label: string;
    icon: ComponentType<{ className?: string }>;
  }> = [
    { scope: 'projects', label: text.projects, icon: FolderKanban },
    { scope: 'scheduled', label: text.scheduled, icon: CalendarClock },
    ...LINK_SCOPES.map((scope) => ({
      scope,
      label: linkConfigs[scope].label,
      icon: linkConfigs[scope].icon,
    })),
    { scope: 'avatars', label: text.avatars, icon: CircleUserRound },
    { scope: 'aesthetics', label: text.aesthetics, icon: SwatchBook },
    { scope: 'members', label: text.members, icon: Users },
  ];

  return (
    <>
      {recentEntries.length > 0 && (
        <MenuGroup heading={text.recent}>
          {recentEntries.map((entry) => (
            <MenuItem
              key={entry.key}
              value={`recent:${entry.key}`}
              onSelect={() => onSelectRecent(entry)}
            >
              <ItemIcon icon={RECENT_ICONS[entry.meta.type] ?? ArrowRight} />
              <span className="min-w-0 flex-1 truncate">
                {entry.meta.label}
              </span>
              <span className="text-muted-foreground shrink-0 text-xs">
                {formatTime(entry.lastUsedAt)}
              </span>
            </MenuItem>
          ))}
        </MenuGroup>
      )}

      {showBrowse && (
        <MenuGroup heading={text.browse}>
          {products.length > 0 && (
            <MenuItem
              value="browse:products"
              onSelect={() => onPushScope('products')}
            >
              <ItemIcon icon={Package} />
              <span>{text.recentProducts}</span>
              <MenuKey className="ml-auto">#</MenuKey>
            </MenuItem>
          )}
          {threads.length > 0 && (
            <MenuItem
              value="browse:threads"
              onSelect={() => onPushScope('threads')}
            >
              <ItemIcon icon={MessageSquare} />
              <span>{text.recentThreads}</span>
              <MenuKey className="ml-auto">@</MenuKey>
            </MenuItem>
          )}
          {canSearchWorkspaceScopes &&
            workspaceScopes.map((item) => (
              <MenuItem
                key={item.scope}
                value={`browse:${item.scope}`}
                onSelect={() => onPushScope(item.scope)}
              >
                <ItemIcon icon={item.icon} />
                <span>{item.label}</span>
              </MenuItem>
            ))}
        </MenuGroup>
      )}

      {gotoItems.length > 0 && (
        <MenuGroup heading={text.goTo}>
          {gotoItems.map((item) => (
            <MenuItem
              key={item.id}
              value={`goto:${item.id}`}
              onSelect={() => onNavigate(item.href, item.label)}
            >
              <ItemIcon icon={item.icon} />
              <span className="min-w-0 flex-1 truncate">{item.label}</span>
              {item.shortcutId && <ShortcutHint shortcutId={item.shortcutId} />}
            </MenuItem>
          ))}
        </MenuGroup>
      )}

      <CommandRows
        commands={quickActions}
        heading={text.quickActions}
        onRun={onRunCommand}
      />

      {orgs.length > 0 && (
        <MenuGroup heading={text.organizations}>
          {orgs.map((org) => (
            <MenuItem
              key={String(org._id)}
              value={`org:${String(org._id)}`}
              onSelect={() => onSelectOrg(org)}
            >
              <ItemIcon icon={Building2} />
              <span className="min-w-0 flex-1 truncate">{org.name}</span>
              {org.role && (
                <span className="text-muted-foreground shrink-0 text-xs">
                  {roleLabels[org.role]}
                </span>
              )}
            </MenuItem>
          ))}
        </MenuGroup>
      )}

      <ProductRows
        products={products}
        heading={shouldSearch ? text.products : text.recentProducts}
        formatTime={formatTime}
        onSelect={onSelectProduct}
      />

      <ThreadRows
        threads={threads}
        heading={shouldSearch ? text.threads : text.recentThreads}
        formatTime={formatTime}
        onSelect={onSelectThread}
      />

      <LinkRows
        links={messages}
        heading={messagesHeading}
        icon={MessagesSquare}
        formatTime={formatTime}
        onSelect={onSelectLink}
      />

      <ProjectRows
        projects={projects}
        heading={text.projects}
        formatTime={formatTime}
        onSelect={onSelectProject}
      />

      <AvatarRows
        avatars={avatars}
        heading={text.avatars}
        onSelect={onSelectAvatar}
      />

      <AestheticRows
        aesthetics={aesthetics}
        heading={text.aesthetics}
        onSelect={onSelectAesthetic}
      />

      <MemberRows
        members={members}
        heading={text.members}
        roleLabels={roleLabels}
        onSelect={onSelectMember}
      />
    </>
  );
}
